import { TipoTelefoneType } from 'app/modules/apps/shared/telefone/tipoTelefone'
import { ConselhoDto } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/core/_models'
import {
  AtendimentoType,
  CargoType,
  ContratoType,
  GenderType,
  GeneroType,
  ID,
  MunicipioList,
  PaisList,
  PlataformaType,
  RacaType,
  Response,
} from '../../../../_metronic/helpers'
import { UsuarioDtoVinculo } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'

export type TentativaInvalida = {
  dataHora: string
  ip?: string
}

export type Auditoria = {
  ultimoAcesso?: string
  tentativasInvalidas?: number
  /** Mais antiga primeiro; o backend guarda só as 20 mais recentes. */
  tentativasInvalidasDetalhe?: TentativaInvalida[]
}

/**
 * Desafio de segundo fator. Enquanto ele vier preenchido, o login não terminou: o backend
 * não emitiu token nem gravou cookie de sessão.
 */
export type MfaDesafio = {
  /** Token opaco de vida curta. Não é um token de acesso e não serve como um. */
  token: string
  /** Se o usuário já tem autenticador. Falso leva ao cadastro obrigatório. */
  cadastrado: boolean
}

/**
 * Resposta do login quando o segundo fator ainda está pendente: não há token nem sessão,
 * apenas o desafio a ser cumprido.
 */
export interface MfaDesafioResponse {
  mfa: MfaDesafio
}

/** Sessão autenticada. `accessToken` é obrigatório: sua presença é o "estou logado". */
export interface AuthTokenResponse {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  trocarSenha?: boolean
  /** Termo de ciência (LGPD) pendente: o backend bloqueia tudo até o aceite em `/termo-ciencia`. */
  termoCienciaPendente?: boolean
  auditoria?: Auditoria
  tempoInatividade?: number
  /** Só vem preenchido na ativação do fator — única vez que os códigos são exibidos. */
  codigosRecuperacao?: string[]
}

/** O login termina em sessão ou em desafio de segundo fator, nunca nos dois. */
export type LoginResponse = AuthTokenResponse | MfaDesafioResponse

export const isMfaDesafio = (resposta: LoginResponse): resposta is MfaDesafioResponse =>
  'mfa' in resposta && Boolean(resposta.mfa)

export interface MfaDtoSetup {
  token: string
}

export interface MfaDtoValidate {
  token: string
  codigo: string
}

export interface MfaSetup {
  /** Segredo em Base32, para quem não consegue ler o QR Code. */
  segredo: string
  /** URI otpauth:// a partir da qual o QR Code é desenhado no cliente. */
  otpauthUri: string
}

export interface Fator2faStatus {
  ativo: boolean
  /** Alguma empresa vinculada exige o segundo fator. */
  exigido: boolean
  podeDesativar: boolean
  codigosDisponiveis: number
}

export interface CodigosRecuperacaoResponse {
  codigosRecuperacao: string[]
}

export interface UsuarioDtoLogout {
  refreshToken?: string
  /** Identificador push (FID do Firebase) do dispositivo/navegador, se aplicável */
  pushToken?: string
}

export type EmpresaDto = {
  id?: ID
  nome?: string
  nomeCnes?: string
  nomeFantasia?: string
  cnpj?: string
  cnes?: string
  ativa?: boolean
  permiteNovoPaciente?: boolean
  permiteApiIntegracao?: boolean
  tiposAtendimento?: AtendimentoType[]
  tiposContrato?: ContratoType[]
  permiteCadastroCliente?: boolean
}

export type NacionalidadeDto = {
  descricao?: number
  id: number
}

export type EstadoDto = {
  sigla?: number
  descricao?: number
  id: number
}

export type DadosAdicionaisDto = {
  nomeMae?: string
  desconheceNomeMae?: boolean
  nomeRepresentante?: string
  raca?: RacaType
  genero?: GeneroType
  nacionalidade?: NacionalidadeDto
  estadoNascimento?: EstadoDto
  municipioNascimento?: MunicipioList
  /** Só vem quando preenchido (`NON_NULL` no backend). */
  paisNascimento?: PaisList
  dtNaturalizacao?: string
  portariaNaturalizacao?: string
}

export type VinculoDto = {
  id?: ID
  empresa?: EmpresaDto
  cargo?: CargoType
  crm: string
  rqe?: string
  paciente?: boolean
  ativo?: boolean
  funcionario?: boolean
  responsavel?: boolean
  medico?: boolean
  gestorAgenda?: boolean
  conselho?: ConselhoDto
  dadosAdicionais?: DadosAdicionaisDto
}

export type PessoaList = {
  id: number
  nomeSocial?: string
  certificadoDigital: boolean
  cns?: string
  primeiroNome: string
  sobreNome: string
  cpf: string
  nome: string
  sexo?: GenderType
  telefone?: string
  dataNascimento?: string
}

export interface PessoaDto {
  id: number
  nome: string
  primeiroNome: string
  sobreNome: string
  nomeSocial?: string
  cpf: string
  cns?: string
  sexo: GenderType
  telefone?: string
  tipoTelefone?: TipoTelefoneType | null
  dataNascimento?: string
  dataNascimentoFormatada?: string
  certificadoDigital?: boolean
}

export interface Message {
  message?: string
}

export type UsuarioPessoa = {
  confirmado?: boolean
  ativo?: boolean
  email?: string
  id?: number
}

export type UsuarioWithImage = UsuarioDtoVinculo & { imagemPerfil?: string }

export type CertificadoDigitalDto = {
  action: 'u' | 'd'
  fileContent?: string
  fileName?: string
  fileType?: string
}

export interface Picture {
  data: string
}

export interface UserAddressModel {
  addressLine: string
  city: string
  state: string
  postCode: string
}

export interface UserCommunicationModel {
  email: boolean
  sms: boolean
  phone: boolean
}

export interface UserEmailSettingsModel {
  emailNotification?: boolean
  sendCopyToPersonalEmail?: boolean
  activityRelatesEmail?: {
    youHaveNewNotifications?: boolean
    youAreSentADirectMessage?: boolean
    someoneAddsYouAsAsAConnection?: boolean
    uponNewOrder?: boolean
    newMembershipApproval?: boolean
    memberRegistration?: boolean
  }
  updatesFromKeenthemes?: {
    newsAboutKeenthemesProductsAndFeatureUpdates?: boolean
    tipsOnGettingMoreOutOfKeen?: boolean
    thingsYouMissedSindeYouLastLoggedIntoKeen?: boolean
    newsAboutStartOnPartnerProductsAndOtherServices?: boolean
    tipsOnStartBusinessProducts?: boolean
  }
}

export interface UserSocialNetworksModel {
  linkedIn: string
  facebook: string
  twitter: string
  instagram: string
}

export type PessoasQueryResponse = Response<Array<PessoaList>>

// export interface UserModel {
//   id: number
//   username: string
//   password: string | undefined
//   email: string
//   first_name: string
//   last_name: string
//   fullname?: string
//   occupation?: string
//   companyName?: string
//   phone?: string
//   roles?: Array<number>
//   pic?: string
//   language?: 'en' | 'de' | 'es' | 'fr' | 'ja' | 'zh' | 'ru'
//   timeZone?: string
//   website?: 'https://keenthemes.com'
//   emailSettings?: UserEmailSettingsModel
//   auth?: AuthModel
//   communication?: UserCommunicationModel
//   address?: UserAddressModel
//   socialNetworks?: UserSocialNetworksModel
// }

export type TokenDtoRefresh = {
  token?: string
  plataforma?: PlataformaType
  empresa?: number
}

export type TokenDtoValidate = {
  token: string
}

export type TokenDtoSignature = {
  token: string
  senha: string
}

export type PasswordValidate = {
  assinatura: boolean
  valido: boolean
}

export type ClearanceDto = {
  certificado?: string
  provedor?: string
  url?: string
}

export type CredentialDto = {
  token: string
  credential: string
}

export type UsuarioDtoUpdate = {
  id: number
  ativo?: boolean
  primeiroNome?: string
  sobreNome?: string
  email?: string
  telefone?: string
  cpf?: string
  cns?: string
  passaporte?: string
  bilheteIdentidadeAngola?: string
  nascimento?: string
  sexo?: GenderType
}

export type VideoChamadaToken = {
  token: string
  channelName: string
  account: string
  subBotUid?: number
  pubBotUid?: number
}

export type PessoaUsuarioList = {
  nomeSocial?: string
  sexo?: GenderType
  dataNascimento?: string
  cns?: string
  usuario?: UsuarioPessoa
  cpf?: string
  nome?: string
  primeiroNome?: string
  dataNascimentoFormatada?: string
  sobreNome?: string
  id: number
}

export type PageResponsePessoaUsuarioList = Response<Array<PessoaUsuarioList>>

export type AvatarDto = {
  action: 'u' | 'd'
  fileContent?: string // Foto do usuário (base64)
  fileName?: string
  fileType?: string
}

export type UsuarioDtoLogin = {
  email: string
  senha: string
  plataforma?: PlataformaType
  empresa?: number
  /** Identificador push (FID do Firebase) do dispositivo/navegador, se aplicável */
  pushToken?: string
}

export type PasswordDtoRecover = {
  email: string
}

export type EmpresaDtoPublica = {
  id: number
  nome: string
  slug: string
  imagem?: string
  logo?: string
}
